import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../../app/providers';
import { href, navigate } from '../../app/router';
import type { Org } from '../../lib/api';
import { Button, Dialog, Input, RadioGroup } from '../../ui';
import { DialogClose } from '../../ui/DialogClose';
import { useBoards, useInvalidateContent, useTemplates } from '../dashboard/queries';
import { nextUntitled } from './default-title';
import { instantiateTemplate, type TemplateChoice } from './instantiate';
import { SYSTEM_TEMPLATES, templateLabels } from './system';
import { TemplatePreview } from './TemplatePreview';
import { FigmaNewBoardDialog } from '../import/figma/FigmaNewBoardDialog';
import { choiceOf, useSystemTemplateFiles } from './TemplatesPage';

type Option = { key: string; name: string; choice: TemplateChoice; preview: boolean };

/** "New board" with the choice of a template (spec §H): blank by default, then the system and the organization's. */
export function NewBoardDialog({ org, projectId, folderId, onClose }: { org: Org; projectId: string; folderId: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const invalidate = useInvalidateContent();
  const files = useSystemTemplateFiles();
  const custom = useTemplates(api.capabilities.boardCopy ? org.id : null);
  const system = SYSTEM_TEMPLATES.filter((s) => s.id === 'blank' || api.capabilities.boardCopy);
  const options: Option[] = [
    ...system.map((s) => ({ key: s.id, name: t(`boardTemplates.names.${s.id}`), choice: choiceOf(s.id), preview: true })),
    ...(custom.data ?? []).map((b) => ({ key: b.id, name: b.title, choice: { kind: 'custom', boardId: b.id } as TemplateChoice, preview: false })),
  ];
  const [selected, setSelected] = useState('blank');
  // New board › Import from Figma (P11b): the Figma wizard takes the dialog's place.
  const [figma, setFigma] = useState(false);
  // null until the user types: the title then follows the chosen template ("Untitled N" for a blank board).
  const [typed, setTyped] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const current = options.find((o) => o.key === selected) ?? options[0]!;
  const siblings = useBoards({ orgId: org.id, projectId });
  const untitled = nextUntitled(t('boardTemplates.untitled'), (siblings.data ?? []).map((b) => b.title));
  const title = typed ?? (current.key === 'blank' ? untitled : current.name.slice(0, 200));
  const input = useRef<HTMLInputElement>(null);
  // The default is selected, so typing replaces it (also once the project's boards load and it becomes "Untitled 2").
  useEffect(() => {
    if (typed === null && input.current === document.activeElement) input.current?.select();
  }, [title, typed]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(false);
    try {
      const name = title.trim() || untitled;
      const { boardId } = await instantiateTemplate({ api, orgId: org.id, projectId, folderId, title: name, choice: current.choice, labels: templateLabels(t) });
      await invalidate();
      onClose();
      navigate(href('board', { boardId }));
    } catch {
      setError(true);
      setBusy(false);
    }
  };

  if (figma) return <FigmaNewBoardDialog orgId={org.id} projectId={projectId} folderId={folderId} onClose={onClose} />;
  return (
    <Dialog title={t('dashboard.newBoard')} onClose={onClose} size="md">
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <label className="flex flex-col gap-1 font-sans text-sm font-medium text-fg">
          {t('dashboard.boardTitle')}
          <Input
            ref={input}
            value={title}
            maxLength={200}
            autoFocus
            onFocus={(e) => typed === null && e.currentTarget.select()}
            onChange={(e) => setTyped(e.target.value)}
          />
        </label>
        <div className="flex flex-col gap-2">
          <p aria-hidden="true" className="font-sans text-sm text-fg">
            {t('boardTemplates.choose')}
          </p>
          <RadioGroup
            variant="cards"
            label={t('boardTemplates.choose')}
            value={selected}
            onValueChange={setSelected}
            options={options.map((o) => ({ value: o.key, label: o.name, preview: o.preview ? <TemplatePreview file={files.get(o.key as never) ?? null} /> : undefined }))}
          />
        </div>
        {error && (
          <p role="alert" className="font-sans text-xs text-danger">
            {t('auth.genericError')}
          </p>
        )}
        <div className="flex justify-end gap-2">
          {api.capabilities.figma && (
            <Button onClick={() => setFigma(true)} className="mr-auto">
              {t('figmaImport.fromNewBoard')}
            </Button>
          )}
          <DialogClose>
            {t('dashboard.cancel')}
          </DialogClose>
          <Button variant="primary" type="submit" disabled={busy}>
            {t('dashboard.create')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
